import { useState, useEffect } from 'react';
import { supabase } from '@/utilities/supabase';

/**
 * Hook to fetch an authenticated image and convert it to a blob URL
 * This allows us to display images from authenticated endpoints in <img> tags or CSS background-image
 */
export const useAuthenticatedImage = (fileId: string | undefined | null, folder: string = 'avatars') => {
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fileId) {
      setImageUrl(undefined);
      return;
    }

    // If it's already a full URL (starts with http), use it directly
    if (fileId.startsWith('http://') || fileId.startsWith('https://')) {
      setImageUrl(fileId);
      return;
    }

    let objectUrl: string | undefined;

    const fetchImage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.access_token) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`/api/assets/stream/${folder}/${fileId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            authorization: `Bearer ${session.access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load image';
        setError(errorMessage);
        console.error('Image fetch error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImage();

    // Cleanup: revoke the object URL when component unmounts or fileId changes
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileId, folder]);

  return {
    imageUrl,
    isLoading,
    error,
  };
};
